import type {
  BusinessCenterCompanyRecord,
  BusinessCenterOnboardingRecord,
  BusinessCenterOnboardingStatus,
  BusinessCenterRoleKey,
  BusinessCenterUserRecord,
  ClientPortalWorkspaceKey,
} from "../../lib/types";
import {
  loadBusinessCenterCompanies,
  loadBusinessCenterCompanyUsers,
  mapCompanyRoleToPermissionRole,
} from "./businessCenterCompanyUsersData";

import { loadBusinessCenterRoleAssignments } from "./businessCenterRolesData";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export const onboardingWorkspaceOptions: Array<{
  key: ClientPortalWorkspaceKey;
  label: string;
  description: string;
}> = [
  {
    key: "kreatethedate",
    label: "Kreatethedate",
    description: "RSVP Hub, Memory Hub, Archive Hub, Memory Media Vault, agreements, invoices, and projects.",
  },
  {
    key: "truevance_connect",
    label: "Truevance Connect",
    description: "Assessments, recommendations, consulting projects, documents, and invoices.",
  },
  {
    key: "ar_recovery",
    label: "AR Recovery",
    description: "Recovery dashboard, customer accounts, statements, letters, promises, and reports.",
  },
];

export const onboardingCompanyTypeOptions: Array<{
  value: BusinessCenterCompanyRecord["companyType"];
  label: string;
  description: string;
}> = [
  {
    value: "parent",
    label: "Parent Account",
    description: "Top-level company account that owns one or more workspaces.",
  },
  {
    value: "subsidiary",
    label: "Subsidiary",
    description: "Child company tied to a parent organization.",
  },
  {
    value: "client_company",
    label: "Client Company",
    description: "External client company scoped to a shared workspace.",
  },
];

export const onboardingStatusOptions: BusinessCenterOnboardingStatus[] = [
  "Not Started",
  "In Progress",
  "Pending",
  "Complete",
];

export const onboardingPortalStatusOptions: Array<BusinessCenterOnboardingRecord["portalStatus"]> = [
  "draft",
  "pending",
  "active",
  "paused",
];

export interface BusinessCenterOnboardingCompletion {
  completed: number;
  total: number;
  percent: number;
  remaining: number;
}

export interface BusinessCenterOnboardingDashboard {
  totalCompanies: number;
  activeOnboardings: number;
  pendingTasks: number;
  completedOnboardings: number;
  averageCompletion: number;
  workspaceCounts: Record<ClientPortalWorkspaceKey, number>;
  statusCounts: Record<BusinessCenterOnboardingStatus, number>;
  portalStatusCounts: Record<BusinessCenterOnboardingRecord["portalStatus"], number>;
}

export interface CreateBusinessCenterOnboardingInput {
  id?: string;
  companyId: string;
  companyName: string;
  companySlug: string;
  companyType: BusinessCenterCompanyRecord["companyType"];
  workspaceAssignments: ClientPortalWorkspaceKey[];
  status?: BusinessCenterOnboardingStatus;
  primaryContactName: string;
  primaryContactEmail: string;
  createdUserCount: number;
  assignedRoleKeys: BusinessCenterRoleKey[];
  portalStatus: BusinessCenterOnboardingRecord["portalStatus"];
  portalUrl?: string;
  branding: BusinessCenterOnboardingRecord["branding"];
  checklist: BusinessCenterOnboardingRecord["checklist"];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

const onboardingRecordsStore = createBusinessCenterOperationalStateStore<BusinessCenterOnboardingRecord[]>({
  moduleKey: "business_center_onboarding_records",
  scope: { scopeKey: "onboarding-center", scopeType: "platform" },
  seed: createSeedOnboardingRecords,
  normalize: (records) => records.map(normalizeOnboardingRecord),
  isEmpty: (records) => !Array.isArray(records) || records.length === 0,
});

export function loadBusinessCenterOnboardingRecords(): BusinessCenterOnboardingRecord[] {
  return onboardingRecordsStore.read();
}

export async function refreshBusinessCenterOnboardingRecords() {
  return onboardingRecordsStore.refresh();
}

export function saveBusinessCenterOnboardingRecords(records: BusinessCenterOnboardingRecord[]) {
  onboardingRecordsStore.write(records);
}

export function upsertBusinessCenterOnboardingRecord(nextRecord: BusinessCenterOnboardingRecord) {
  const current = loadBusinessCenterOnboardingRecords();
  const normalized = normalizeOnboardingRecord({ ...nextRecord, updatedAt: new Date().toISOString() });
  const exists = current.some((record) => record.id === normalized.id);
  const records = exists
    ? current.map((record) => (record.id === normalized.id ? normalized : record))
    : [normalized, ...current];
  saveBusinessCenterOnboardingRecords(records);
  return records;
}

export function updateBusinessCenterOnboardingRecord(
  recordId: string,
  changes: Partial<BusinessCenterOnboardingRecord>,
) {
  const current = loadBusinessCenterOnboardingRecords();
  const records = current.map((record) =>
    record.id === recordId
      ? normalizeOnboardingRecord({
          ...record,
          ...changes,
          updatedAt: new Date().toISOString(),
        })
      : record,
  );
  saveBusinessCenterOnboardingRecords(records);
  return records;
}

export function createBusinessCenterOnboardingRecord(
  input: CreateBusinessCenterOnboardingInput,
): BusinessCenterOnboardingRecord {
  const now = input.createdAt || new Date().toISOString();
  const companySlug = slugify(input.companySlug || input.companyName || input.companyId);
  const portalUrl = input.portalUrl || buildOnboardingPortalUrl(companySlug, input.workspaceAssignments);
  const normalizedChecklist = normalizeChecklist(input.checklist);
  const derivedStatus = input.status || deriveOnboardingStatus(normalizedChecklist);

  return normalizeOnboardingRecord({
    id: input.id || `onboarding-${input.companyId}`,
    companyId: input.companyId,
    companyName: input.companyName || "Untitled Company",
    companyType: input.companyType,
    workspaceAssignments: ensureUniqueWorkspaces(input.workspaceAssignments),
    status: derivedStatus,
    primaryContactName: input.primaryContactName || "Primary Contact",
    primaryContactEmail: input.primaryContactEmail || "contact@example.com",
    createdUserCount: Math.max(0, Math.floor(input.createdUserCount || 0)),
    assignedRoleKeys: ensureUniqueRoles(input.assignedRoleKeys),
    portalStatus: input.portalStatus,
    portalUrl,
    branding: {
      companyName: input.branding.companyName || input.companyName || "Untitled Company",
      logo: input.branding.logo || "",
      primaryColor: input.branding.primaryColor || "#1F2937",
      accentColor: input.branding.accentColor || "#06B6D4",
      contactPhone: input.branding.contactPhone || "",
      contactEmail: input.branding.contactEmail || input.primaryContactEmail || "contact@example.com",
    },
    checklist: normalizedChecklist,
    notes: input.notes || "",
    createdAt: now,
    updatedAt: input.updatedAt || now,
  });
}

export function buildOnboardingPortalUrl(
  companySlug: string,
  workspaceAssignments: ClientPortalWorkspaceKey[],
): string {
  const workspaceValue = (workspaceAssignments.length > 0 ? workspaceAssignments : ["truevance_connect"]).join(",");
  const params = new URLSearchParams({
    company: slugify(companySlug || "untitled-company"),
    workspace: workspaceValue,
  });
  return `/client/portal?${params.toString()}`;
}

export function calculateOnboardingCompletion(record: BusinessCenterOnboardingRecord): BusinessCenterOnboardingCompletion {
  const checklistValues = Object.values(record.checklist);
  const completed = checklistValues.filter(Boolean).length;
  const total = checklistValues.length;
  const remaining = total - completed;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  return { completed, total, remaining, percent };
}

export function buildBusinessCenterOnboardingDashboard(
  records: BusinessCenterOnboardingRecord[],
  companies: BusinessCenterCompanyRecord[] = loadBusinessCenterCompanies(),
  users: BusinessCenterUserRecord[] = loadBusinessCenterCompanyUsers(),
): BusinessCenterOnboardingDashboard {
  const nonParentCompanies = companies.filter((company) => company.companyType !== "parent");
  const workspaceCounts: Record<ClientPortalWorkspaceKey, number> = {
    kreatethedate: 0,
    truevance_connect: 0,
    ar_recovery: 0,
  };
  const statusCounts: Record<BusinessCenterOnboardingStatus, number> = {
    "Not Started": 0,
    "In Progress": 0,
    Pending: 0,
    Complete: 0,
  };
  const portalStatusCounts: Record<BusinessCenterOnboardingRecord["portalStatus"], number> = {
    draft: 0,
    pending: 0,
    active: 0,
    paused: 0,
  };

  let pendingTasks = 0;
  let completionTotal = 0;

  records.forEach((record) => {
    statusCounts[record.status] += 1;
    portalStatusCounts[record.portalStatus] += 1;
    const completion = calculateOnboardingCompletion(record);
    pendingTasks += completion.remaining;
    completionTotal += completion.percent;

    record.workspaceAssignments.forEach((workspace) => {
      workspaceCounts[workspace] += 1;
    });
  });

  const activeOnboardings = records.filter((record) => record.status === "In Progress" || record.status === "Pending").length;
  const completedOnboardings = records.filter((record) => record.status === "Complete").length;
  const averageCompletion = records.length > 0 ? Math.round(completionTotal / records.length) : 0;

  // Keep the model aware of the broader workspace even if the UI only renders onboarding records.
  void users.length;

  return {
    totalCompanies: nonParentCompanies.length,
    activeOnboardings,
    pendingTasks,
    completedOnboardings,
    averageCompletion,
    workspaceCounts,
    statusCounts,
    portalStatusCounts,
  };
}

function createSeedOnboardingRecords(): BusinessCenterOnboardingRecord[] {
  const companies = loadBusinessCenterCompanies().filter((company) => company.companyType !== "parent");
  const users = loadBusinessCenterCompanyUsers();
  const roleAssignments = loadBusinessCenterRoleAssignments();
  const now = new Date().toISOString();

  return companies.map((company, index) => createSeedOnboardingRecord(company, users, roleAssignments, now, index));
}

function createSeedOnboardingRecord(
  company: BusinessCenterCompanyRecord,
  users: BusinessCenterUserRecord[],
  roleAssignments: ReturnType<typeof loadBusinessCenterRoleAssignments>,
  timestamp: string,
  index: number,
): BusinessCenterOnboardingRecord {
  const companyUsers = users.filter((user) => user.companyId === company.id);
  const workspaceAssignments = resolveWorkspacesForCompany(company);
  const primaryContact = companyUsers[0];
  const roleKeys = Array.from(new Set(companyUsers.map((user) => mapCompanyRoleToPermissionRole(user.role))));
  const hasActivePortal = companyUsers.some((user) => user.portalAccessStatus === "active");

  const checklist = normalizeChecklist({
    companyCreated: true,
    usersAdded: companyUsers.length > 0,
    rolesAssigned: roleKeys.length > 0 || roleAssignments.some((assignment) => assignment.userEmail.toLowerCase().includes(company.companySlug)),
    portalActivated: hasActivePortal || index === 0,
    brandingApplied: Boolean(company.companyName.trim()),
    billingSetup: company.brand === "kreatethedate" || company.brand === "truevance_connect",
    documentsUploaded: company.brand === "kreatethedate",
  });

  return createBusinessCenterOnboardingRecord({
    id: `onboarding-${company.id}`,
    companyId: company.id,
    companyName: company.companyName,
    companySlug: company.companySlug,
    companyType: company.companyType,
    workspaceAssignments,
    status: deriveOnboardingStatus(checklist),
    primaryContactName: primaryContact?.fullName || `${company.companyName} Primary Contact`,
    primaryContactEmail: primaryContact?.email || `hello@${company.companySlug}.com`,
    createdUserCount: companyUsers.length,
    assignedRoleKeys: roleKeys,
    portalStatus: hasActivePortal ? "active" : index === 0 ? "pending" : "draft",
    portalUrl: buildOnboardingPortalUrl(company.companySlug, workspaceAssignments),
    branding: {
      companyName: company.companyName,
      logo: `${company.companySlug}-logo.svg`,
      primaryColor: resolveBrandPrimaryColor(company.brand),
      accentColor: resolveBrandAccentColor(company.brand),
      contactPhone: "",
      contactEmail: primaryContact?.email || `hello@${company.companySlug}.com`,
    },
    checklist,
    notes:
      index === 0
        ? "Completed foundation with active workspace access."
        : index === 1
          ? "Portal launch in progress while workspace configuration is refined."
          : "Queued for onboarding activation.",
    createdAt: timestamp,
    updatedAt: timestamp,
  });
}

function normalizeOnboardingRecord(record: BusinessCenterOnboardingRecord): BusinessCenterOnboardingRecord {
  const checklist = normalizeChecklist(record.checklist);
  return {
    ...record,
    id: record.id || `onboarding-${record.companyId}`,
    companyId: record.companyId,
    companyName: record.companyName || "Untitled Company",
    companyType:
      record.companyType === "parent" || record.companyType === "subsidiary" || record.companyType === "client_company"
        ? record.companyType
        : "client_company",
    workspaceAssignments: ensureUniqueWorkspaces(record.workspaceAssignments),
    status: onboardingStatusOptions.includes(record.status) ? record.status : deriveOnboardingStatus(checklist),
    primaryContactName: record.primaryContactName || "Primary Contact",
    primaryContactEmail: record.primaryContactEmail || "contact@example.com",
    createdUserCount: Math.max(0, Math.floor(record.createdUserCount || 0)),
    assignedRoleKeys: ensureUniqueRoles(record.assignedRoleKeys),
    portalStatus:
      record.portalStatus === "draft" || record.portalStatus === "pending" || record.portalStatus === "active" || record.portalStatus === "paused"
        ? record.portalStatus
        : "draft",
    portalUrl: record.portalUrl || buildOnboardingPortalUrl(record.companyName || record.companyId, record.workspaceAssignments || []),
    branding: {
      companyName: record.branding?.companyName || record.companyName || "Untitled Company",
      logo: record.branding?.logo || "",
      primaryColor: record.branding?.primaryColor || "#1F2937",
      accentColor: record.branding?.accentColor || "#06B6D4",
      contactPhone: record.branding?.contactPhone || "",
      contactEmail: record.branding?.contactEmail || record.primaryContactEmail || "contact@example.com",
    },
    checklist,
    notes: record.notes || "",
    createdAt: record.createdAt || new Date().toISOString(),
    updatedAt: record.updatedAt || new Date().toISOString(),
  };
}

function normalizeChecklist(checklist: BusinessCenterOnboardingRecord["checklist"]): BusinessCenterOnboardingRecord["checklist"] {
  return {
    companyCreated: Boolean(checklist?.companyCreated),
    usersAdded: Boolean(checklist?.usersAdded),
    rolesAssigned: Boolean(checklist?.rolesAssigned),
    portalActivated: Boolean(checklist?.portalActivated),
    brandingApplied: Boolean(checklist?.brandingApplied),
    billingSetup: Boolean(checklist?.billingSetup),
    documentsUploaded: Boolean(checklist?.documentsUploaded),
  };
}

function deriveOnboardingStatus(checklist: BusinessCenterOnboardingRecord["checklist"]): BusinessCenterOnboardingStatus {
  const completion = calculateChecklistCompletion(checklist);
  if (completion.percent === 100) return "Complete";
  if (completion.completed === 0) return "Not Started";
  if (completion.completed <= 3) return "Pending";
  return "In Progress";
}

function calculateChecklistCompletion(checklist: BusinessCenterOnboardingRecord["checklist"]) {
  const values = Object.values(normalizeChecklist(checklist));
  const completed = values.filter(Boolean).length;
  const total = values.length;
  return {
    completed,
    total,
    percent: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
}

function ensureUniqueWorkspaces(workspaces: ClientPortalWorkspaceKey[]): ClientPortalWorkspaceKey[] {
  const values = Array.isArray(workspaces) ? workspaces : [];
  const allowed: ClientPortalWorkspaceKey[] = ["kreatethedate", "truevance_connect", "ar_recovery"];
  const unique = Array.from(new Set(values.filter((workspace): workspace is ClientPortalWorkspaceKey => allowed.includes(workspace))));
  return unique.length > 0 ? unique : ["truevance_connect"];
}

function ensureUniqueRoles(roleKeys: BusinessCenterRoleKey[]) {
  const allowed: BusinessCenterRoleKey[] = ["admin", "manager", "team_member", "client", "viewer"];
  return Array.from(new Set((Array.isArray(roleKeys) ? roleKeys : []).filter((role): role is BusinessCenterRoleKey => allowed.includes(role))));
}

function resolveWorkspacesForCompany(company: BusinessCenterCompanyRecord): ClientPortalWorkspaceKey[] {
  if (company.brand === "kreatethedate") return ["kreatethedate"];
  if (company.brand === "truevance_connect") return ["truevance_connect"];
  if (company.brand === "ar_recovery") return ["ar_recovery"];
  return ["truevance_connect"];
}

function resolveBrandPrimaryColor(brand: string) {
  if (brand === "kreatethedate") return "#FF6B35";
  if (brand === "ar_recovery") return "#7C3AED";
  return "#06B6D4";
}

function resolveBrandAccentColor(brand: string) {
  if (brand === "kreatethedate") return "#14B8A6";
  if (brand === "ar_recovery") return "#EC4899";
  return "#22C55E";
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    || "untitled-company";
}
